import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import RestUtilities from '../../services/RestUtilities';
import {
  Calendar as BigCalendar,
  dayjsLocalizer,
  View,
  Views,
} from 'react-big-calendar';
import { Box, Button, FormControl, MenuItem } from '@mui/material';
import {
  liveArtistsState,
  loadingState,
  toastState,
} from '../../state/RecoilState';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { lightOrDark } from '../../Functions';
import { Artist, CalendarEvent } from '../../@types/api';
import { CustomToolbar } from '../../components/Calendar/CustomToolbar';
import SmallSelect from '../../components/Common/SmallSelect';
import CreateEventDialogue from '../../components/Calendar/CreateEventDialogue';
import ViewEventDialogue from '../../components/Calendar/ViewEventDialogue';
import { dbTimeFormat } from '../../Constants';
import YearView from '../../components/Calendar/YearView';

import 'react-big-calendar/lib/css/react-big-calendar.css'; // Correct import for CSS
import '../../styles/calendar.scss';
import { useGetLiveArtists } from '../../hooks/useGetLiveArtists';

dayjs.extend(updateLocale);
const localizer = dayjsLocalizer(dayjs);
dayjs.updateLocale('en', { weekStart: 1 });

const CalendarView = () => {
  const setToast = useSetRecoilState(toastState);
  const artists = useRecoilValue(liveArtistsState);
  const setLoading = useSetRecoilState(loadingState);
  const clickRef = useRef<string | number | undefined>(undefined);
  const [data, setData] = useState<CalendarEvent[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<View | 'year'>(Views.MONTH);
  const [artistId, setArtistId] = useState<number>(0);
  const [selectedEvent, setSelectedEvent] = useState<undefined | CalendarEvent>(
    undefined
  );
  const [showAddEvent, setShowAddEvent] = useState(false);

  useGetLiveArtists();

  const getAllData = useCallback(async () => {
    setLoading(true);
    const endpoint = view === 'year' ? 'GetYear' : 'Get';
    const resp = await RestUtilities.get(
      `/api/calendar/${endpoint}/${dayjs(currentDate).format(
        'YYYY-MM-DD'
      )}?artistId=${artistId}`
    );
    if (resp.ok) {
      const resData: CalendarEvent[] = resp.content.map((x: any) => ({
        ...x,
        start: new Date(x.startDate),
        end: new Date(x.endDate),
      }));

      setData((prevData) => {
        const eventSet = new Set(prevData.map((event) => event.id));
        const mergedData = [...prevData];

        resData.forEach((event) => {
          if (!eventSet.has(event.id)) {
            mergedData.push(event);
            eventSet.add(event.id);
          }
        });

        return mergedData;
      });
    }
    setLoading(false);
  }, [setLoading, view, currentDate, artistId]);

  useEffect(() => {
    getAllData();
  }, [getAllData]);

  useEffect(() => {
    return () => {
      window.clearTimeout(clickRef?.current);
    };
  }, []);

  const eventPropGetter = useCallback((event, start, end, isSelected) => {
    return {
      ...(isSelected && {
        style: {
          backgroundColor: '#000',
        },
      }),
      ...(!isSelected && {
        style: {
          color: lightOrDark(event.color) === 'light' ? 'black' : 'white',
          backgroundColor: event.color,
        },
      }),
    };
  }, []);

  const onSelectEvent = useCallback((calEvent) => {
    window.clearTimeout(clickRef?.current);
    clickRef.current = window.setTimeout(() => {
      setSelectedEvent(calEvent);
    }, 250);
  }, []);

  const { components } = useMemo(
    () => ({
      components: {
        toolbar: CustomToolbar,
      },
    }),
    []
  );

  const onArtistSelect = async (e: any) => {
    setArtistId(e.target.value);
  };

  const deleteEvent = async () => {
    if (!selectedEvent) return;
    const res = await RestUtilities.delete(
      `/api/calendar/delete/${selectedEvent.gigId}`
    );
    if (res.ok) {
      setSelectedEvent(undefined);
      await getAllData();
      setToast('Event was deleted');
    } else {
      setToast('Event failed to be deleted');
    }
  };

  const addEvent = async (event: CalendarEvent) => {
    const res = await RestUtilities.post('/api/calendar/post', {
      ...event,
      startDate: dayjs(event.start).format(dbTimeFormat),
      endDate: dayjs(event.end).format(dbTimeFormat),
    });
    if (res.ok) {
      setShowAddEvent(false);
      setToast('Event was added');
      await getAllData();
    } else {
      setToast('Event failed to be added');
    }
  };

  if (!data) return null;

  const artistName = () => {
    const selected = artists.find((a: Artist) => a.artistId === artistId) as
      | Artist
      | undefined;
    return !!selected ? selected.artistName : '';
  };
  return (
    <Box mb={3}>
      <Box
        className='calendar-controls-title'
        sx={{
          display: 'none',
          position: 'absolute',
          left: 20,
          fontSize: '20px',
          fontWeight: 'bold',
        }}
      >
        {artistName()}
      </Box>
      <Box
        display='flex'
        alignItems='center'
        justifyContent='flex-end'
        sx={{ position: 'absolute', right: 20 }}
        className='calendar-controls'
      >
        <div className='calendar-controls-buttons'>
          <Button
            variant='contained'
            color='secondary'
            size='small'
            onClick={() => setShowAddEvent(true)}
            sx={{ mr: 1 }}
          >
            Add Event
          </Button>
          <FormControl>
            <SmallSelect
              labelId='artist-select-label'
              id='demo-simple-select'
              onChange={onArtistSelect}
              value={artistId}
              variant='outlined'
            >
              <MenuItem key={'id_0'} value={0}>
                All Live Artists
              </MenuItem>
              {artists?.map((a: Artist) => {
                return (
                  <MenuItem key={`id_${a.artistId}`} value={a.artistId}>
                    {a.artistName}
                  </MenuItem>
                );
              })}
            </SmallSelect>
          </FormControl>
        </div>
      </Box>
      <BigCalendar
        date={currentDate}
        style={{ minHeight: '50vh', maxWidth: '100%' }}
        localizer={localizer}
        events={
          artistId
            ? data.filter((a: CalendarEvent) => a.artistId === artistId)
            : data
        }
        view={view as View}
        onView={(view: View) => setView(view)}
        views={
          {
            year: YearView,
            month: true,
            day: false,
            week: true,
          } as any
        }
        messages={{ year: 'Year' } as any}
        onNavigate={(d: Date) => setCurrentDate(d)}
        eventPropGetter={eventPropGetter}
        onSelectEvent={onSelectEvent}
        showAllEvents
        components={components}
        toolbar={true}
        culture='en-gb'
      />

      <ViewEventDialogue
        selectedEvent={selectedEvent}
        deleteEvent={deleteEvent}
        setSelectedEvent={setSelectedEvent}
      />

      <CreateEventDialogue
        artists={artists}
        showAddEvent={showAddEvent}
        setShowAddEvent={setShowAddEvent}
        addEvent={addEvent}
      />
    </Box>
  );
};

export default CalendarView;
